import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

import { Lead } from "@/types";

import { appDate } from "@/utils/date";

import AppTable from "@/components/AppTable";
import AppTableContainer from "@/components/AppTableContainer";
import AppTableHead from "@/components/AppTable/AppTableHead";
import AppTableRow from "@/components/AppTable/AppTableRow";
import AppTableBody from "@/components/AppTable/AppTableBody";
import AppTableCell from "@/components/AppTable/AppTableCell";
import AppTableHeaderCell from "@/components/AppTable/AppTableHeaderCell";
import AppLink from "@/components/AppLink";
import AppBox from "@/components/AppBox";
import AppButton from "@/components/AppButton";
import AppText from "@/components/AppText";
import StatusSelect from '@/components/Lead/StatusSelect';
import AppIcon from "@/components/AppIcon";
import AppTooltip from "@/components/AppTooltip";
import AppSwitch from "@/components/AppSwitch";

interface LeadsTableProps {
  tableRows: Lead[];
  onClickLink: (row: any) => void;
  onChangeSetting: (leadId: number, field: string, currentValue: boolean) => void;
  onStatusChange: (leadId: number, status: string) => void;
  page: number;
  totalPages: number;
  setPage: (page: number) => void;
}

const LeadsTable: React.FC<LeadsTableProps> = ({
  tableRows = [],
  onClickLink,
  onStatusChange,
  onChangeSetting,
}) => {
  const navigate = useNavigate();

  const handleClickRow = (lead: { id: number }) => {
    navigate(`/lead/${lead.id}`);
  };

  return (
    <AppTableContainer className="h-full flex flex-col overflow-auto">
      <AppTable>
        <AppTableHead>
          <AppTableRow>
            <AppTableHeaderCell>Lead Name</AppTableHeaderCell>
            <AppTableHeaderCell>Phone</AppTableHeaderCell>
            <AppTableHeaderCell>Type</AppTableHeaderCell>
            <AppTableHeaderCell>Date</AppTableHeaderCell>
            <AppTableHeaderCell>Status</AppTableHeaderCell>
            <AppTableHeaderCell>Pause Follow-Ups</AppTableHeaderCell>
            <AppTableHeaderCell className="w-[360px]">
              <AppBox className="pl-10">
                Action
              </AppBox>
            </AppTableHeaderCell>
          </AppTableRow>
        </AppTableHead>

        <AppTableBody className="h-full">
          {tableRows.length ? (
            tableRows.map((row, index) => (
              <AppTableRow
                key={index}
                className="hover:bg-gray-100 cursor-pointer"
                onClick={() => handleClickRow(row)}
              >
                {/* Name */}
                <AppTableCell>
                  <AppBox width={180} className="py-6">
                    <AppLink to={`/lead/${row.id}`}>
                      {row.name || `LSA ${row.type} Lead`}
                    </AppLink>
                  </AppBox>
                </AppTableCell>

                {/* Phone */}
                <AppTableCell>
                  <AppText variant={ !row?.phone ? 'caption' : 'body1'}>{row?.phone || 'empty'}</AppText>
                </AppTableCell>

                {/* Type */}
                <AppTableCell>
                  <AppBox width={120}>
                    {row.type}
                  </AppBox>
                </AppTableCell>

                {/* Date */}
                <AppTableCell>
                  <AppBox width={160}>
                    { appDate(row.created_at) }
                  </AppBox>
                </AppTableCell>
                
                {/* Status Select */}
                <AppTableCell onClick={(e) => e.stopPropagation()} className="justify-center">
                  <AppBox width={220}>
                    <StatusSelect
                      value={row.status || ''}
                      onChange={(status) => onStatusChange(row.id, status)}
                      dropdownProps={{
                        position: "auto"
                      }}
                    />
                  </AppBox>
                </AppTableCell>

                {/* Pause Follow-Ups */}
                <AppTableCell onClick={(e) => e.stopPropagation()} className="justify-center">
                  <AppSwitch
                    checked={row.pause_followups}
                    onChange={() => onChangeSetting(row.id, "pause_followups", row.pause_followups)}
                  />
                </AppTableCell>

                {/* Actions */}
                <AppTableCell>
                  <AppBox className="flex items-center gap-2 pl-10 relative">
                    {/* Bell Icon */}
                    <AppBox className="absolute -ml-8">
                      {row.new && row.type !== "Call" && (
                        <AppTooltip title="New message from lead">
                          <AppIcon 
                            name="bellRing" 
                            className="text-error"
                            size={20}
                          />
                        </AppTooltip>
                      )}
                    </AppBox>
                    
                    {/* Action Buttons */}
                    <AppBox className="flex gap-2 w-full justify-end">
                      {(row.type === "Message" || row.type === "Missed Call") && (
                        <AppBox className="w-[160px]">
                          <AppButton variant="primary" className="gap-2">
                            See Conversation
                          </AppButton>
                        </AppBox>
                      )}

                      <AppBox className="w-[160px]">
                        <AppButton
                          onClick={(e) => {
                            e.stopPropagation();
                            onClickLink(row);
                          }}
                          disabled={!row?.shortlink}
                        >
                          See Lead in LSA
                        </AppButton>
                      </AppBox>
                    </AppBox>
                  </AppBox>
                </AppTableCell>
              </AppTableRow>
            ))
          ) : (
            <AppTableRow>
              <AppTableCell colSpan={5} className="text-center">
                No results
              </AppTableCell>
            </AppTableRow>
          )}
        </AppTableBody>
      </AppTable>
    </AppTableContainer>
  );
};

export default LeadsTable;